import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import * as PortalUserActions from '@/common/components/ecorr-store/portal-user/portal-user.action';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { EcorrUtilsService } from '@/common/utils/ecorr-utils.service';
import { SurveysService } from '@/surveys/states/surveys/surveys.service';
import { Injectable, OnDestroy, signal, computed, inject, DestroyRef } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, NavigationEnd, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { LocalStorage, LocalStorageService, SessionStorage, SessionStorageService } from 'ngx-webstorage';
import { lastValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EcorrGuardService } from './ecorr-guard.service';
import { Profile } from './profile';

@Injectable({
    providedIn: 'root'
})
export class EcorrGuard implements CanActivate, OnDestroy {
    private destroyRef = inject(DestroyRef);
    private router = inject(Router);
    private localStorageService = inject(LocalStorageService);
    private sessionStorageService = inject(SessionStorageService);
    private store = inject(Store<PortalUserState>);
    private ecorrGuardService = inject(EcorrGuardService);
    private ecorrUtilsService = inject(EcorrUtilsService);
    private surveysService = inject(SurveysService);

    @LocalStorage('returnUrl') returnUrl: string;
    @SessionStorage('userAccountId') userAccountId: number;
    
    // Signal-based state management
    user = signal<PortalUser | null>(null);
    returnUrlKey = signal<string>('returnUrl');

    // Computed properties
    currentUser = computed(() => this.user());
    hasValidUser = computed(() => {
        const currentUser = this.currentUser();
        return currentUser && 
               currentUser.userAccountId && 
               currentUser.userAccountId > 0 && 
               currentUser.roleTypeId && 
               currentUser.respondentTypes && 
               currentUser.firstName && 
               currentUser.lastName && 
               currentUser.email && 
               currentUser.phone;
    });

    constructor() {
        this.init();
    }

    private init(): void {
        this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((user: PortalUser) => {
                this.user.set(user);
                
                this.router.events
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(e => {
                        if (e instanceof NavigationEnd && this.currentUser()) {
                            const currentUser = this.currentUser();
                            if (currentUser?.userAccountId && currentUser.userAccountId > 0) {
                                if (e.url !== '/surveys' && !currentUser.activeSurveyCards) {
                                    this.surveysService.getSurveys(currentUser.userAccountId)
                                        .pipe(takeUntilDestroyed(this.destroyRef))
                                        .subscribe({
                                            next: (data: any[]) => {
                                                let showSendMessage = true;
                                                data.forEach((item: any) => {
                                                    if (item.statusIndicator === '2' || item.reportingEnabled !== 'N') {
                                                        showSendMessage = false;
                                                        return;
                                                    }
                                                });

                                                this.store.dispatch(PortalUserActions.setUser({
                                                    payload: {
                                                        portalUser: {
                                                            ...currentUser,
                                                            showSendMessage: showSendMessage,
                                                            activeSurveyCards: [],
                                                        }
                                                    }
                                                }));
                                            }
                                        });
                                }
                            }
                        }
                    });
            });
    }

    async canActivate(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {
        const currentUser = this.currentUser();

        if (this.hasValidUser()) {
            return true;
        } else {
            if (!this.returnUrl) {
                this.localStorageService.store(this.returnUrlKey(), state.url);
            }
            
            try {
                const data: Profile = await lastValueFrom(
                    this.ecorrGuardService.getProfileByUserAccountId(this.userAccountId)
                );
                
                const user: PortalUser = {
                    companyId: null,
                    userAccountId: data.userAccountId,
                    roleTypeId: data.roleTypeId,
                    showSendMessage: true,
                    respondentTypes: data.respondentTypes,
                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phone: data.phone,
                    addressLine_1: null,
                    addressLine_2: null,
                    addressLine_3: null,
                    city: null,
                    state: null,
                    county: null,
                    country: null,
                    postalCd: null,
                    companyName: null,
                    activeSurveyCards: null,
                    inActiveSurveyCards: null
                };
                
                this.store.dispatch(PortalUserActions.setUser({ payload: { portalUser: { ...user } } }));
                this.sessionStorageService.store('userAccountId', data.userAccountId);
                
                // Setup logout observer with automatic cleanup
                this.localStorageService.store('footprint', true);
                this.localStorageService.observe('footprint')
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe((footprint) => {
                        if (!footprint) {
                            this.ecorrUtilsService.logout();
                        }
                    });

                if (this.returnUrl) {
                    const dest = this.returnUrl;
                    this.localStorageService.clear(this.returnUrlKey());
                    this.router.navigateByUrl(dest);
                }
                return true;
                
            } catch (error) {
                console.error('Guard error:', error);
                this.router.navigate(['/portal']);
                return false;
            }
        }
    }

    ngOnDestroy(): void {
        // No need for manual subscription cleanup with takeUntilDestroyed
        // The destroyRef will automatically handle cleanup
    }
}
